import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { SessionResponse } from '../types/sessions';
import { formatDateOfBirthUs } from '../utils/dateDisplay';
import '../styles/sessions-page.css';

interface ViewSessionModalProps {
  session: SessionResponse;
  onClose: () => void;
}

function parseHours(hours: string | undefined): { hour: string; minute: string } {
  if (!hours) return { hour: '', minute: '' };
  const hourMatch = hours.match(/(\d+)\s*hour/i);
  const minMatch = hours.match(/(\d+)\s*min/i);
  return {
    hour: hourMatch ? hourMatch[1].padStart(2, '0') : '',
    minute: minMatch ? minMatch[1].padStart(2, '0') : '',
  };
}

function normalizeUrl(url: string): string {
  if (!url || !url.trim()) return '';
  const trimmedUrl = url.trim();
  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    return trimmedUrl;
  }
  return `https://${trimmedUrl}`;
}

const ViewSessionModal: React.FC<ViewSessionModalProps> = ({ session, onClose }) => {
  const isoDate = session.date.split('T')[0];
  const dateText = formatDateOfBirthUs(isoDate) ?? isoDate;
  const { hour, minute } = useMemo(() => parseHours(session.hours), [session.hours]);
  const recordingLink = session.recordingLink?.trim() ?? '';
  const notesLink = session.notesLink?.trim() ?? '';
  const notes = session.notes ?? '';
  const uploads = session.uploads ?? [];

  const openLink = (url: string) => {
    const normalized = normalizeUrl(url);
    if (!normalized) return;
    window.open(normalized, '_blank', 'noopener,noreferrer');
  };

  return createPortal(
    <div className="session-modal-overlay">
      <div className="session-modal" data-testid="view-session-modal">
        <div className="session-modal-header">
          <h2 className="session-modal-title">Session Details</h2>
          <button
            className="session-modal-close"
            onClick={onClose}
            aria-label="Close modal"
            data-testid="close-view-modal-button"
          >
            ×
          </button>
        </div>

        <div className="session-modal-content">
          <div className="session-form session-form--readonly">
            <div className="session-form-group">
              <label htmlFor="view-staffName" className="session-form-label-small">
                Session lead
              </label>
              <input
                type="text"
                id="view-staffName"
                value={session.staffName}
                className="session-form-input session-form-input-readonly"
                readOnly
                data-testid="view-staff-name"
              />
            </div>

            <div className="session-datetime-section">
              <h3 className="session-section-title">Enter date and time of session</h3>
              <div className="session-datetime-grid">
                <div className="session-form-group">
                  <label htmlFor="view-date" className="session-form-label-caps">
                    ENTER DATE
                  </label>
                  <input
                    type="text"
                    id="view-date"
                    value={dateText}
                    className="session-form-input session-form-input-readonly"
                    readOnly
                    data-testid="view-date"
                  />
                </div>

                <div className="session-form-group">
                  <label className="session-form-label-caps">ENTER TIME</label>
                  <div className="session-time-inputs" data-testid="view-hours">
                    <div className="session-time-field">
                      <input
                        type="text"
                        value={hour || '00'}
                        className="session-time-input session-form-input-readonly"
                        readOnly
                        data-testid="view-hour-input"
                        aria-label="Hours"
                      />
                      <span className="session-time-label">Hour</span>
                    </div>
                    <span className="session-time-separator">:</span>
                    <div className="session-time-field">
                      <input
                        type="text"
                        value={minute || '00'}
                        className="session-time-input session-form-input-readonly"
                        readOnly
                        data-testid="view-minute-input"
                        aria-label="Minutes"
                      />
                      <span className="session-time-label">Minute</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="session-links-section">
              <h3 className="session-section-title">Add session links</h3>
              <div className="session-links-grid">
                <div className="session-form-group">
                  <label htmlFor="view-recordingLink" className="session-form-label-icon">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M7.5 3.5L3.5 7.5L7.5 11.5M8.5 3.5L12.5 7.5L8.5 11.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    RECORDING
                  </label>
                  {recordingLink ? (
                    <button
                      type="button"
                      id="view-recordingLink"
                      className="session-form-input session-form-input-readonly session-form-link-button"
                      onClick={() => openLink(recordingLink)}
                      data-testid="view-recording-link"
                      title="Open recording link"
                    >
                      {recordingLink}
                    </button>
                  ) : (
                    <input
                      type="text"
                      id="view-recordingLink"
                      value=""
                      placeholder="—"
                      className="session-form-input session-form-input-readonly"
                      readOnly
                      data-testid="view-recording-link"
                    />
                  )}
                </div>

                <div className="session-form-group">
                  <label htmlFor="view-notesLink" className="session-form-label-icon">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M7.5 3.5L3.5 7.5L7.5 11.5M8.5 3.5L12.5 7.5L8.5 11.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    NOTES
                  </label>
                  {notesLink ? (
                    <button
                      type="button"
                      id="view-notesLink"
                      className="session-form-input session-form-input-readonly session-form-link-button"
                      onClick={() => openLink(notesLink)}
                      data-testid="view-notes-link"
                      title="Open notes link"
                    >
                      {notesLink}
                    </button>
                  ) : (
                    <input
                      type="text"
                      id="view-notesLink"
                      value=""
                      placeholder="—"
                      className="session-form-input session-form-input-readonly"
                      readOnly
                      data-testid="view-notes-link"
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="session-form-group">
              <label htmlFor="view-notes" className="session-form-label-caps">
                ADD SESSION NOTES
              </label>
              <textarea
                id="view-notes"
                value={notes}
                className="session-form-textarea session-form-input-readonly"
                readOnly
                placeholder="—"
                data-testid="view-notes"
              />
            </div>

            {uploads.length > 0 && (
              <div className="session-form-group">
                <label className="session-form-label-caps">UPLOADS</label>
                <div className="view-session-uploads" data-testid="view-uploads">
                  {uploads.map((upload, index) => (
                    <div key={`${upload}-${index}`} className="view-session-upload-item">
                      {upload}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="session-modal-footer">
          <button
            type="button"
            className="session-modal-button secondary"
            onClick={onClose}
            data-testid="close-button"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ViewSessionModal;
